import cn from 'classnames';
import { FC, useCallback, useState } from 'react';
import { ReactSVG } from 'react-svg';
import _styles from './styles.module.scss';

type Props = {
  className?: string;
  onClick?: () => void;
  pointer?: boolean;
  rotate?: '90' | '180' | '270' | '360';
  color?:
    | 'primaryDark'
    | 'primaryText'
    | 'secondary'
    | 'primary'
    | 'third'
    | 'valid'
    | 'error'
    | 'white'
    | 'red'
    | 'brand'
    | string;
  size?: 'xs' | 'sm' | 'md' | 'mdm' | 'lg' | 'xl' | 'xxl' | 'xxxl';
  src: string;
  top?: '1' | '2' | '3' | '4';
  id?: string;
  styles?: Record<string, string>;
  display?: 'block' | 'inline-block';
  name?: string;
  nameClassName?: string;
  /** ReactSVG wrapper, default 'div'; use 'span' inside a <p> — note svg-size mixin only targets div, so don't combine with `size` */
  wrapper?: 'div' | 'span';
  title?: string;
};

export const SvgIcon: FC<Props> = ({
  className,
  nameClassName,
  pointer,
  onClick,
  rotate,
  color,
  size,
  src,
  top,
  id,
  styles = {},
  display,
  name,
  wrapper,
  title,
}) => {
  const [svgExists, setSvgExists] = useState<boolean>(true);

  // Wrap onError to defer setState, preventing React #185
  const handleError = useCallback((): void => {
    // defer to next tick so React doesn't complain about setState during render
    setTimeout(() => setSvgExists(false), 0);
  }, []);

  const applyA11y = useCallback(
    (svg: SVGElement): void => {
      if (title) return;
      svg.removeAttribute('role');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
    },
    [title]
  );

  const wrapperStyle = Object.keys(styles).length ? styles : undefined;

  // fallback if SVG fails
  if (!svgExists) {
    return <span className={cn(_styles.provider_name, nameClassName)}>{name || ''}</span>;
  }

  const wrapperClassName = cn(
    _styles.svgIcon,
    {
      [_styles[`svgIcon_rotate_${rotate}`]]: rotate,
      [_styles[`svgIcon_color_${color}`]]: color,
      [_styles[`svgIcon_size_${size}`]]: size,
      [_styles[`svgIcon_top_${top}`]]: top,
      [_styles['svgIcon_pointer']]: pointer,
      [_styles[`svgIcon_display_${display}`]]: display,
    },
    className
  );

  return (
    <span style={wrapperStyle} onClick={onClick}>
      <ReactSVG
        className={wrapperClassName}
        src={src}
        id={id}
        onError={handleError}
        wrapper={wrapper}
        title={title}
        beforeInjection={applyA11y}
      />
    </span>
  );
};
